import React, {useState, useEffect} from "react";
import {
    Box, Paper, TextField, FormControl, InputLabel, Select, MenuItem,
    Button, Chip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {getLandlordId} from "../../services/api/client";
import {propertiesApi} from "../../services/api/properties";

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
const firstOfYear = `${today.getFullYear()}-01-01`;
const lastOfYear = `${today.getFullYear()}-12-31`;

const PRESETS = [
    {label: "This Month", start: firstOfMonth, end: lastOfMonth},
    {label: "Last Month", start: new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split("T")[0], end: new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split("T")[0]},
    {label: "This Year", start: firstOfYear, end: lastOfYear},
    {label: "Last Year", start: `${today.getFullYear() - 1}-01-01`, end: `${today.getFullYear() - 1}-12-31`},
];

export default function ReportFilters({onApply, onDownloadPDF, downloading, hideDateStart, defaultStart, defaultEnd}) {
    const [startDate, setStartDate] = useState(defaultStart || firstOfMonth);
    const [endDate, setEndDate] = useState(defaultEnd || lastOfMonth);
    const [propertyId, setPropertyId] = useState("");
    const [properties, setProperties] = useState([]);

    useEffect(() => {
        fetchProperties();
    }, []);

    useEffect(() => {
        // Auto-apply on mount
        handleApply();
    }, []);

    const fetchProperties = async () => {
        try {
            const response = await propertiesApi.getAll(getLandlordId());
            if (response.ok && Array.isArray(response.data)) {
                setProperties(response.data);
            }
        } catch (err) {
            console.error("Failed to fetch properties:", err);
        }
    };

    const handleApply = () => {
        const params = {landlord_id: getLandlordId()};
        if (!hideDateStart) params.start_date = startDate;
        params.end_date = endDate;
        if (propertyId) params.property_id = parseInt(propertyId);
        onApply(params);
    };

    const handlePreset = (preset) => {
        setStartDate(preset.start);
        setEndDate(preset.end);
    };

    const handlePDF = () => {
        const params = {landlord_id: getLandlordId()};
        if (!hideDateStart) params.start_date = startDate;
        params.end_date = endDate;
        if (propertyId) params.property_id = parseInt(propertyId);
        onDownloadPDF(params);
    };

    return (
        <Paper sx={{p: 2, mb: 3, borderRadius: 2}}>
            <Box sx={{display: "flex", gap: 1, flexWrap: "wrap", mb: 2}}>
                {PRESETS.map(p => (
                    <Chip key={p.label} label={p.label} size="small" variant="outlined" clickable
                        onClick={() => handlePreset(p)}
                        sx={{fontSize: "12px", "&:hover": {bgcolor: "#e3f2fd"}}}/>
                ))}
            </Box>
            <Box sx={{display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center"}}>
                {!hideDateStart && (
                    <TextField label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{shrink: true}} size="small" sx={{width: 170}}/>
                )}
                <TextField label={hideDateStart ? "As of Date" : "End Date"} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{shrink: true}} size="small" sx={{width: 170}}/>
                <FormControl size="small" sx={{minWidth: 180}}>
                    <InputLabel>Property</InputLabel>
                    <Select value={propertyId} label="Property" onChange={(e) => setPropertyId(e.target.value)}>
                        <MenuItem value="">All Properties</MenuItem>
                        {properties.map(p => <MenuItem key={p.property_id} value={p.property_id}>{p.property_name}</MenuItem>)}
                    </Select>
                </FormControl>
                <Button variant="contained" startIcon={<FilterListIcon/>} onClick={handleApply} size="small"
                    sx={{background: "linear-gradient(45deg, #2C5283, #1E3A63)", textTransform: "none"}}>
                    Apply
                </Button>
                <Button variant="outlined" startIcon={<PictureAsPdfIcon/>} onClick={handlePDF} disabled={downloading} size="small"
                    sx={{textTransform: "none", borderColor: "#c62828", color: "#c62828"}}>
                    {downloading ? "Generating..." : "Download PDF"}
                </Button>
            </Box>
        </Paper>
    );
}
